import getLocal from '@/app/utils/getLocal';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import classNames from 'classnames';
import editorEventsBus from 'lowcode/utils/editorEventsBus';
import React, { useState, useEffect } from 'react';
import gtm from '@/utils/gtm';
import EfBundlesCalculator from '../ef-bundles-calculator';
import EfImg from '../ef-img';
import EfLink from '../ef-link';
import './index.scss';
import { langText } from './langText';
import { formatOffText } from '@/app/eflayout/Header/DropBox/DropBoxSearch';
import cookie from 'react-cookies';

interface EfBundlesCardAIItemSubProps {
    /**
     * 子产品-图片
     */
    img?: string;
    /**
     * 产品图片
     */
    variantImg?: string;
    /**
     * 子产品所属父产品的variantId（避免加减数量时混乱）
     */
    parentVariantId?: string;
    /**
     * 子产品-产品 productId
     */
    productId: string;
    /**
     * 子产品-产品 variantId
     */
    variantId: string;
    /**
     * 子产品-实际价格
     */
    price: number;
    /**
     * 子产品-划线价格
     */
    compareAtPrice: number;
    /**
     * 子产品-默认购买数量
     */
    quantity?: number;
    /**
     * 子产品-库存
     */
    inventoryQuantity?: number;
    /**
     * 子产品-开始展示时间
     */
    timeStart?: string;
    /**
     * 子产品-结束展示时间
     */
    timeEnd?: string;
    /**
     * 文案-主标题
     */
    productTitle?: string;
    /**
     * 子产品-SKU标题
     */
    skuTitle?: string;
}

export interface EfBundlesCardAIProps {
    /**
     * 主图
     */
    img?: string;
    /**
     * 产品 productId
     */
    productId: string;
    /**
     * 产品 variantId
     */
    variantId: string;
    /**
     * 购买链接
     */
    buyHref?: string;
    /**
     * 产品实际价格
     */
    discountPrice: number;
    /**
     * 产品划线价格
     */
    originalPrice: number;
    /**
     * 默认购买数量
     */
    quantity: number;
    /**
     * 库存
     */
    inventoryQuantity?: number;
    /**
     * 产品开始展示时间
     */
    timeStart?: string;
    /**
     * 产品结束展示时间
     */
    timeEnd?: string;
    /**
     * 优惠券-优惠码
     */
    coupon?: string;
    /**
     * 优惠券-左侧提示文案
     */
    couponText?: string;
    /**
     * 优惠券-优惠浮窗提示语
     */
    couponTips?: string;
    /**
     * 是否点击后添加到购物车
     */
    buyInCart?: boolean;
    /**
     * buy 不直接触发,回到回调中
     */
    isBuyInCallback?: boolean;
    /**
     * isBuyInCallback 的回调
     */
    buyLinkCallback?: (item: any, buyHref: string) => void;
    /**
     * 文案-大标题
     */
    title?: string;
    /**
     * 文案-主标题
     */
    name?: string;
    /**
     * 文案-特性
     */
    power?: string;
    /**
     * 文案-描述
     */
    desc?: string;
    /**
     * 更多详情链接
     */
    moreLink?: string;

    /**
     * buy now 按钮自定义回调
     */
    callbackBuy?: any;
    /**
     * 子产品集
     */
    subs?: EfBundlesCardAIItemSubProps[];
    /**
     * 用于埋点的list_name
     */
    list_name?: string;
    /**
     * 埋点-learn more
     */
    gtm_learn_more?: any;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 自定义分期的icon
     */
    affirmIconUrl?: string;
    /**
     * 是否需要Affirm的图标和文案
     */
    hasAffirm?: boolean;
    /**
     * 传入的Affirm文案
     */
    affirmText?: string;
    /**
     * variants
     */
    variants?: any;
}

/**
 * 维护:谢保林
 *
 * 用于 Bundles 的商品卡片
 */
const EfBundlesCardAI: React.FC<EfBundlesCardAIProps> = ({
    variantId,
    buyHref,
    discountPrice,
    originalPrice,
    quantity,
    inventoryQuantity,
    timeStart,
    timeEnd,
    coupon,
    couponText,
    couponTips,
    buyInCart,
    title,
    name,
    power,
    desc,
    moreLink,
    isBuyInCallback,
    callbackBuy,
    subs,
    className,

    variants,
}) => {
    const item = {
        variantId,
        buyHref,
        discountPrice,
        originalPrice,
        quantity,
        inventoryQuantity,
        timeStart,
        timeEnd,
        coupon,
        couponText,
        couponTips,
        buyInCart,
        title,
        name,
        power,
        desc,
        moreLink,
        subs,
        callbackBuy,
        isBuyInCallback,
        variants,
    };

    const { locale = 'us' } = getLocal();
    const textA = langText[locale] || langText['us']; //卡片内非传入文本
    const prefixCls = `ef-bundles-card-ai`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
    });

    /**
     * 格式化 variants 数据，使其适用于 EfBundlesCalculator 组件
     */
    const formatVariantsData = (variants: EfBundlesCardAIItemSubProps[]) => {
        return variants.map((variant: EfBundlesCardAIItemSubProps) => ({
            ...variant,
            name: variant.productTitle || '',
            discountPrice: variant.price || 0,
            maxCanBuyCount: variant.inventoryQuantity || 999,
            inventoryQuantity: variant.inventoryQuantity || 999,
        }));
    };
    const [variantsData, setVariantsData] = useState(formatVariantsData(item.variants || [])); // 用于保存选择的子产品

    // 监听 variants 变化，确保数据同步更新
    useEffect(() => {
        if (variants && variants.length > 0) {
            const formatted = formatVariantsData(variants);
            setVariantsData(formatted);
        }
    }, [variants]);

    const totalPrice = variantsData.reduce((sum: number, v: any) => sum + Number(v?.price) * v.quantity, 0);
    const totalCompare = variantsData.reduce((sum: number, v: any) => sum + Number(v?.compareAtPrice || v?.price) * v.quantity, 0);
    const [totalBundle, setTotalBundle] = useState({
        discount: totalPrice.toFixed(2),
        original: totalCompare.toFixed(2),
    }); //卡片总价

    const getInitBuyHref = (data: any) => {
        let valStr = ''; // 副产品
        for (let i = 0; i < data.length; i++) {
            const itemB = data[i];
            const variantId = itemB.variantId;
            const quantity = itemB.quantity;
            if (variantId && quantity) {
                const linkFlag = i === data.length - 1 ? '' : ',';
                valStr += `${variantId}:${quantity}${linkFlag}`;
            }
        }
        const hrefAll = `https://${locale}.ecoflow.com/cart/${valStr}?channel=buy_button`;
        return hrefAll;
    };

    const initHrefString = getInitBuyHref(item.variants);
    const [initHref, setInitHref] = useState(initHrefString); //购买链接

    // 设置购买链接
    const setBuyHrefFn = (subsB2: any) => {
        let valStr = ''; // 副产品
        for (let i = 0; i < subsB2.length; i++) {
            const itemB = subsB2[i];
            const variantId = itemB.variantId;
            const quantity = itemB.quantity;
            if (variantId && quantity) {
                const linkFlag = i === variantsData.length - 1 ? '' : ',';

                valStr += `${variantId}:${quantity}${linkFlag}`;
            }
        }
        // const href1 = `${item.variantId}:1`; //主产品
        const hrefAll = `https://${locale}.ecoflow.com/cart/${valStr}?channel=buy_button`;
        setInitHref(hrefAll);
    };

    // 数量发生变化
    const numChange = (index2: number, quantity: number) => {
        const variants2 = JSON.parse(JSON.stringify(variantsData));
        variants2[index2].quantity = quantity || 0;

        // 确保数据格式保持一致性，重新应用格式化
        const formattedVariants = formatVariantsData(variants2);

        const totalObj = getSubTotalFn(formattedVariants);
        setBuyHrefFn(formattedVariants);

        setTotalBundle({
            discount: totalObj.discount.toFixed(2),
            original: totalObj.original.toFixed(2),
        });
        setVariantsData(formattedVariants);
    };
    // 获取子项的总价
    const getSubTotalFn = (subs2: any) => {
        let discount = 0;
        let original = 0;
        for (let i = 0; i < subs2.length; i++) {
            const item2 = subs2[i];
            const quantity = item2.quantity || 0;

            discount += quantity * (item2?.price || 0);
            original += quantity * (item2?.compareAtPrice || item2?.price || 0);
        }

        return {
            discount,
            original,
        };
    };

    // 点击购买--打开购物车
    const buyInCartHandle = () => {
        // 收集所有有数量的产品
        const productsToAdd = variantsData
            .filter((variant) => variant.quantity && variant.quantity > 0)
            .map((variant) => ({
                item: {
                    // 必需字段
                    variantId: variant?.variantId,
                    productId: variant?.productId,

                    // 产品信息
                    name: variant?.productTitle || variant?.skuTitle || '',
                    title: variant?.productTitle || variant?.skuTitle || '',
                    img: variant?.variantImg || variant.img,

                    // 价格信息
                    price: variant?.price,
                    discountPrice: variant?.price,
                    originalPrice: variant?.compareAtPrice,
                    compareAtPrice: variant?.compareAtPrice,

                    // 库存和限制
                    inventoryQuantity: variant?.inventoryQuantity,
                    maxCanBuyCount: variant?.maxCanBuyCount || variant?.inventoryQuantity || 999,

                    // 其他字段
                    parentVariantId: variant?.parentVariantId,
                },
                quantity: variant?.quantity,
            }));

        if (productsToAdd.length > 0) {
            const expires = new Date();
            expires.setDate(expires.getDate() + 7);

            const cookieOptions = {
                path: '/',
                domain: '.ecoflow.com',
                expires,
            };

            cookie.save('ai_entry_type', 'AI Recommend Search_Shopify Product Page', { path: '/', domain: '.ecoflow.com' });
            cookie.save('ai_jump_url', 'Official Cart Popup', { path: '/', domain: '.ecoflow.com' });
            cookie.save('display_type', 'AI Recommend Search_Recommend Bundles_Official Cart Popup', cookieOptions);
            try {
                // TODO: 埋点 - 批量添加到购物车
                const totalQuantity = productsToAdd.reduce((sum, product) => sum + (product.quantity || 0), 0);
                const totalPrice = productsToAdd.reduce((sum, product) => sum + product.item.price * (product.quantity || 0), 0);
                const productNames = productsToAdd.map((product) => product.item.name).join(', ');

                gtm.push({
                    event: 'uaEvent',
                    event_name: 'bundle_add_to_cart',
                    list_name: 'none',
                    product_name: productNames,
                    price: totalPrice,
                    quantity: totalQuantity,
                    button_name: textA.link_buy || 'Add to Cart',
                    tag: 'none',
                    display_type: 'Recommend Bundles',
                    module_referral: 'AI Recommend Search',
                    jump_module: 'Official Cart Popup',
                });

                // 添加到购物车
                editorEventsBus.emit('efshopingcart-add', productsToAdd, null, 'AI Recommend Search');
            } catch (error) {
                console.error('添加到购物车时发生错误:', error);
            }
        } else {
            console.log('没有选择任何产品或数量为0');
        }
    };

    const handleBuyClick = () => {
        const expires = new Date();
        expires.setDate(expires.getDate() + 7);

        const cookieOptions = {
            path: '/',
            domain: '.ecoflow.com',
            expires,
        };

        cookie.save('ai_entry_type', 'AI Recommend Search_Recommend Bundles', { path: '/', domain: '.ecoflow.com' });
        cookie.save('ai_jump_url', initHref, { path: '/', domain: '.ecoflow.com' });
        cookie.save('display_type', 'AI Recommend Search_Recommend Bundles_Checkout Page', cookieOptions);

        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: textA.BuyNow || 'Buy Now',
            display_type: 'Recommend Bundles',
            module_referral: 'AI Recommend Search',
            jump_module: initHref || '',
        });
    };

    const finalCouponText = 100 - Math.round((parseFloat(totalBundle.discount) / parseFloat(totalBundle.original)) * 100);
    const finalOffText = formatOffText(finalCouponText, locale);

    return (
        <div className={classes}>
            {totalBundle.discount !== totalBundle.original && (
                <div className={`${prefixCls}-coupons`}>
                    <div className={`${prefixCls}-coupons-title`}>{finalOffText}</div>
                </div>
            )}

            {variantsData && variantsData?.length > 0 && (
                <>
                    <div className={`${prefixCls}-subs`}>
                        {/* {subsB.map((sub: EfBundlesCardAIItemSubProps, index2: number) => { */}
                        {variantsData?.slice(0, 3)?.map((sub: EfBundlesCardAIItemSubProps, index2: number) => {
                            return (
                                <div key={index2} className={`${prefixCls}-sub-item`}>
                                    <div className={`${prefixCls}-sub-item-img`}>
                                        <EfImg pc={sub?.variantImg || sub?.img || ''} />
                                    </div>
                                    <div className={`${prefixCls}-sub-item-msg`}>
                                        {(sub?.productTitle || sub?.skuTitle) && (
                                            <p className={`${prefixCls}-sub-item-title`}>{sub?.productTitle || sub?.skuTitle}</p>
                                        )}
                                        {/* {sub?.desc && <p className={`${prefixCls}-sub-item-desc`}>{sub.desc}</p>} */}
                                        <div className={`${prefixCls}-sub-item-control`}>
                                            {sub.price && (
                                                <p className={`${prefixCls}-sub-item-price`}>
                                                    <span className={`${prefixCls}-sub-item-price-discount`}>{FormatMoneyLocal(sub?.price, locale)}</span>
                                                    {sub?.compareAtPrice && sub?.price !== sub?.compareAtPrice && (
                                                        <span className={`${prefixCls}-sub-item-price-original`}>
                                                            {FormatMoneyLocal(sub?.compareAtPrice, locale)}
                                                        </span>
                                                    )}
                                                </p>
                                            )}
                                            <EfBundlesCalculator
                                                defaultQuantity={sub?.quantity || 0}
                                                item={sub}
                                                callback={(quantity: number) => {
                                                    numChange(index2, quantity);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            <div className={`${prefixCls}-footer`}>
                <div className={`${prefixCls}-total`}>
                    <span className={`${prefixCls}-total-title`}>{textA.total}</span>
                    <div className={`${prefixCls}-total-container`}>
                        <span className={`${prefixCls}-total-discount`}>{FormatMoneyLocal(parseFloat(totalBundle.discount), locale)}</span>
                        {totalBundle.discount !== totalBundle.original && (
                            <span className={`${prefixCls}-total-original`}>{FormatMoneyLocal(parseFloat(totalBundle.original), locale)}</span>
                        )}
                    </div>
                </div>
                <div className={`${prefixCls}-links`}>
                    <button className={`${prefixCls}-link ${prefixCls}-link-more`} onClick={() => buyInCartHandle()}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M6.00098 1.25763C6.27712 1.25763 6.50098 1.48149 6.50098 1.75763V5.49982H10.2441C10.5201 5.49999 10.744 5.72389 10.7441 5.99982C10.7441 6.27585 10.5201 6.49964 10.2441 6.49982H6.50098V10.243C6.50082 10.519 6.27702 10.743 6.00098 10.743C5.72502 10.7429 5.50114 10.5189 5.50098 10.243V6.49982H1.75879C1.48265 6.49982 1.25879 6.27596 1.25879 5.99982C1.25891 5.72378 1.48272 5.49982 1.75879 5.49982H5.50098V1.75763C5.50098 1.48155 5.72492 1.25774 6.00098 1.25763Z"
                                fill="black"
                            />
                        </svg>
                        {textA.link_buy}
                    </button>
                    <EfLink className={`${prefixCls}-link ${prefixCls}-link-buy`} href={initHref} onClick={handleBuyClick}>
                        {textA.BuyNow}
                    </EfLink>
                </div>
            </div>
        </div>
    );
};

export default EfBundlesCardAI;
